import { FC, useMemo } from "react";
import clsx from "clsx";

import { EquipmentItem } from "../../model/types";

import styles from "./EquipmentsList.module.scss";

type EquipmentsListProps = {
  equipments: EquipmentItem[];
  className?: string;
};

type GroupedEquipment = {
  equipment_id: number;
  equipment_title: string;
  equipment_title_photo: string | null;
  details: {
    detail_id: number;
    detail_name: string;
    detail_type: string;
    detail_title_photo: string | null;
    options: {
      option_id: number | null;
      option_value: string | null;
    }[];
  }[];
};

const groupEquipments = (equipments: EquipmentItem[]): GroupedEquipment[] => {
  const grouped: Record<number, GroupedEquipment> = {};

  equipments.forEach((item) => {
    if (!grouped[item.equipment_id]) {
      grouped[item.equipment_id] = {
        equipment_id: item.equipment_id,
        equipment_title: item.equipment_title,
        equipment_title_photo: item.equipment_title_photo,
        details: [],
      };
    }

    const equipment = grouped[item.equipment_id];
    let detail = equipment.details.find((d) => d.detail_id === item.detail_id);

    if (!detail) {
      detail = {
        detail_id: item.detail_id,
        detail_name: item.detail_name,
        detail_type: item.detail_type,
        detail_title_photo: item.detail_title_photo,
        options: [],
      };
      equipment.details.push(detail);
    }

    // Add option if it doesn't exist
    // For null option_id, we only need one entry per detail
    // For specific options, add each unique option_id
    if (item.option_id === null) {
      const hasNullOption = detail.options.some((opt) => opt.option_id === null);
      if (!hasNullOption) {
        detail.options.push({
          option_id: item.option_id,
          option_value: item.option_value,
        });
      }
    } else {
      const optionExists = detail.options.some(
        (opt) => opt.option_id === item.option_id,
      );
      if (!optionExists) {
        detail.options.push({
          option_id: item.option_id,
          option_value: item.option_value,
        });
      }
    }
  });

  return Object.values(grouped);
};

export const EquipmentsList: FC<EquipmentsListProps> = ({
  equipments,
  className,
}) => {
  const grouped = useMemo(() => groupEquipments(equipments), [equipments]);

  if (!equipments.length) {
    return (
      <div className={clsx(styles.wrapper, className)}>
        <div className={styles.empty}>Оборудование пока не указано</div>
      </div>
    );
  }

  return (
    <div className={clsx(styles.wrapper, className)}>
      <div className={styles.list}>
        {grouped.map((equipment) => (
          <div key={equipment.equipment_id} className={styles.item}>
            <div className={styles.itemHeader}>
              <h4 className={styles.itemTitle}>{equipment.equipment_title}</h4>
            </div>

            <div className={styles.details}>
              {equipment.details.map((detail) => (
                <div key={detail.detail_id} className={styles.detail}>
                  <div className={styles.detailHeader}>
                    <span className={styles.detailName}>{detail.detail_name}</span>
                  </div>

                  {detail.options.length > 0 ? (
                    <div className={styles.options}>
                      {detail.options
                        .filter((opt) => opt.option_value !== null)
                        .map((opt, idx) => (
                          <span key={opt.option_id ?? idx} className={styles.option}>
                            {opt.option_value}
                          </span>
                        ))}
                      {detail.options.every((opt) => opt.option_value === null) && (
                        <span className={styles.optionEmpty}>Указано</span>
                      )}
                    </div>
                  ) : (
                    <div className={styles.options}>
                      <span className={styles.optionEmpty}>Указано</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

